import React, { useEffect, useState, useMemo } from 'react'
import { authService } from '../../../modules/identity/services/auth'
import type { User } from '../../../types'
import { useWindowWidth } from '../../../lib/useWindowWidth'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, ComposedChart, Area, Line, Legend,
} from 'recharts'
import {
  BarChart2, RefreshCw, TrendingDown, TrendingUp, Minus, Clock,
  Percent, CalendarClock, IndianRupee, Users,
  ShieldCheck, Download, FileText, XCircle, ChevronDown, ChevronRight, Sparkles,
  AlertTriangle, Target,
} from 'lucide-react'
import {
  reportingService,
  type LostSalesData,
  type RenewalSplitData,
  type RenewalBookHealthData,
  type RenewalBookTrendPoint,
  type AvgDaysData,
  type OdDiscountData,
  type RenewalForecastData,
  type CashPolicyRegisterData,
  type FraudSummaryData,
  type NcCoverageData,
  type FraudLogEntry,
  type LostLeadsSummaryData,
  type LostLeadsListItem,
  type TelecallerLostPerformance,
  type TelecallerAIScoreData,
  type TelecallerPerformanceData,
  type CoachingPlanData,
} from '../services/reports'

// ── helpers ─────────────────────────────────────────────────────────────────

// Mirrors backend/app/modules/reporting/services/telecaller_ai_performance.py MIN_ANALYZED_CALLS_DEFAULT
const MIN_AI_SCORE_CALLS = 20

const fmt = (n: number) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR', maximumFractionDigits: 0,
  }).format(n)

const fmtDate = (s: string | null) =>
  s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'

const fmtDateTime = (s: string | null) => {
  if (!s) return '—'
  const d = new Date(s)
  const date = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
  const time = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })
  return `${date} · ${time}`
}

const SEVERITY_BADGE: Record<string, string> = {
  LOW:      'badge-info',
  MEDIUM:   'badge-warning',
  HIGH:     'badge-orange',
  CRITICAL: 'badge-danger',
}
const ALERT_STATUS_BADGE: Record<string, string> = {
  OPEN:          'badge-danger',
  INVESTIGATING: 'badge-warning',
  RESOLVED:      'badge-success',
  FALSE_POSITIVE:'badge-neutral',
}

// ── Tab definition ───────────────────────────────────────────────────────────

type Tab = 'sales' | 'od-discount' | 'pipeline' | 'fraud' | 'exports' | 'lost-leads' | 'telecaller-performance'

const ALL_TABS: { key: Tab; label: string; icon: React.ElementType; addonFlag?: string; roles?: string[] }[] = [
  { key: 'sales',       label: 'Sales Analysis',     icon: TrendingDown                                                                    },
  { key: 'od-discount', label: 'OD Discount',         icon: Percent                                                                         },
  { key: 'lost-leads',  label: 'Lost Leads',          icon: XCircle,       roles: ['DEALER_PRINCIPAL', 'INSURANCE_HEAD', 'BACKEND_ADMIN']    },
  { key: 'telecaller-performance', label: 'Telecaller Performance', icon: Users,   roles: ['DEALER_PRINCIPAL', 'INSURANCE_HEAD', 'BACKEND_ADMIN'] },
  { key: 'pipeline',    label: 'Pipeline',             icon: CalendarClock, addonFlag: 'renewal_forecast_enabled'                             },
  { key: 'fraud',       label: 'Fraud & Compliance',  icon: ShieldCheck,   addonFlag: 'fraud_detection_enabled'                              },
  { key: 'exports',     label: 'Exports',              icon: Download,      addonFlag: 'data_export_enabled'                                 },
]

const DISPOSITION_BADGE: Record<string, string> = {
  NOT_INTERESTED:           'badge-danger',
  CUSTOMER_BOOKED_ELSEWHERE:'badge-warning',
  SOLD_VEHICLE:             'badge-orange',
  DUE_DATE_LAPSED:          'badge-warning',
  WRONG_NUMBER:             'badge-neutral',
  NO_ANSWER:                'badge-neutral',
  IN_PROCESS:               'badge-info',
  POLICY_ISSUED:            'badge-success',
  CALLBACK_REQUESTED:       'badge-info',
  ASKING_FOR_DISCOUNT:      'badge-orange',
  NONE:                     'badge-neutral',
}

const STATUS_BADGE: Record<string, string> = {
  LOST:                  'badge-danger',
  LOST_RENEWED_ELSEWHERE:'badge-warning',
  NOT_RESPONSIVE:        'badge-neutral',
}

// ── Shared sub-components ────────────────────────────────────────────────────

function KpiTile({
  label, value, accent, accentBg, icon: Icon, sub,
}: {
  label: string; value: string | number; accent: string; accentBg: string; icon: React.ElementType
  sub?: string
}) {
  return (
    <div className="card flex items-start gap-3">
      <div className="rounded-lg p-2 shrink-0" style={{ background: accentBg }}>
        <Icon className="h-4 w-4" style={{ color: accent }} />
      </div>
      <div>
        <p className="stat-card-label">{label}</p>
        <p className="stat-card-value mt-0.5" style={{ color: accent }}>{value}</p>
        {sub && <p className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>{sub}</p>}
      </div>
    </div>
  )
}

function SectionHeader({ title, onRefresh, loading }: {
  title: string; onRefresh?: () => void; loading?: boolean
}) {
  return (
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-sm font-semibold uppercase tracking-wider"
        style={{ color: 'var(--text-tertiary)' }}>
        {title}
      </h2>
      {onRefresh && (
        <button onClick={onRefresh} disabled={loading} className="btn-icon" title="Refresh">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      )}
    </div>
  )
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function ReportsPage() {
  const windowWidth = useWindowWidth()
  const isMobile = windowWidth < 640
  const chartBarSize = isMobile ? 20 : 40
  const chartTickSize = isMobile ? 10 : 12

  const currentUser = authService.getCurrentUser() as (User & { tenant_feature_flags?: Record<string, unknown> }) | null
  const featureFlags = currentUser?.tenant_feature_flags ?? {}
  const userRole = currentUser?.role ?? ''
  const TABS = useMemo(
    () => ALL_TABS.filter(t =>
      (!t.addonFlag || !!featureFlags[t.addonFlag]) &&
      (!t.roles || t.roles.includes(userRole as string))
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )
  const aiExtractionEnabled = !!(featureFlags as Record<string, unknown>)['ai_extraction_enabled']
  const aiOnDemand = !!(featureFlags as Record<string, unknown>)['ai_analysis_on_demand']

  const [tab, setTab] = useState<Tab>('sales')

  // Sales tab state
  const [lostSalesDays, setLostSalesDays] = useState(30)
  const [lostSales,     setLostSales]     = useState<LostSalesData | null>(null)
  const [renewalSplit,  setRenewalSplit]   = useState<RenewalSplitData | null>(null)
  const [avgDays,       setAvgDays]        = useState<AvgDaysData | null>(null)
  const [bookHealth,    setBookHealth]     = useState<RenewalBookHealthData | null>(null)
  const [bookTrend,     setBookTrend]      = useState<RenewalBookTrendPoint[] | null>(null)
  const [loadingSales,  setLoadingSales]   = useState(false)

  // OD Discount tab state
  const [odDiscount,    setOdDiscount]     = useState<OdDiscountData | null>(null)
  const [loadingOd,     setLoadingOd]      = useState(false)

  // Pipeline tab state
  const [forecast,      setForecast]       = useState<RenewalForecastData | null>(null)
  const [cashRegister,  setCashRegister]   = useState<CashPolicyRegisterData | null>(null)
  const [loadingPipe,   setLoadingPipe]    = useState(false)

  // Fraud tab state
  const [fraudSummary,  setFraudSummary]   = useState<FraudSummaryData | null>(null)
  const [ncCoverage,    setNcCoverage]     = useState<NcCoverageData | null>(null)
  const [fraudLog,      setFraudLog]       = useState<FraudLogEntry[] | null>(null)
  const [loadingFraud,  setLoadingFraud]   = useState(false)

  // Export tab state
  const [exportDays,    setExportDays]     = useState(7)
  const [exporting,     setExporting]      = useState(false)

  // Lost Leads tab state
  const [lostDays,        setLostDays]        = useState(30)
  const [lostSummary,     setLostSummary]     = useState<LostLeadsSummaryData | null>(null)
  const [lostList,        setLostList]        = useState<LostLeadsListItem[]>([])
  const [lostTotal,       setLostTotal]       = useState(0)
  const [lostPage,        setLostPage]        = useState(1)
  const [lostFilterStatus,     setLostFilterStatus]     = useState('')
  const [lostFilterDisp,       setLostFilterDisp]       = useState('')
  const [loadingLost,     setLoadingLost]     = useState(false)
  const [expandedLead,    setExpandedLead]    = useState<number | null>(null)
  const [narrativeLoading, setNarrativeLoading] = useState<number | null>(null)
  const [narrativeCache,  setNarrativeCache]  = useState<Record<number, string>>({})
  // keyed by user_id
  const [tcPerfCache,     setTcPerfCache]     = useState<Record<number, TelecallerLostPerformance>>({})

  // Telecaller Performance tab state (operational stats + AI coaching, merged)
  const [tcStats,      setTcStats]      = useState<TelecallerPerformanceData[] | null>(null)
  const [aiScores,     setAiScores]     = useState<TelecallerAIScoreData[] | null>(null)
  const [loadingAi,    setLoadingAi]    = useState(false)
  const [coachingPlanLoading, setCoachingPlanLoading] = useState<number | null>(null)
  const [coachingPlanHistory, setCoachingPlanHistory] = useState<Record<number, CoachingPlanData[]>>({})
  const [coachingHistoryOpen, setCoachingHistoryOpen] = useState<Record<number, boolean>>({})
  const [tcPerfLoading,   setTcPerfLoading]   = useState<number | null>(null)
  const [expandedTc,   setExpandedTc]   = useState<number | null>(null)
  const [tcSearch,     setTcSearch]     = useState('')
  const [tcSort,       setTcSort]       = useState<'ai_score' | 'conversion' | 'name'>('ai_score')

  // ── Load functions (lazy per tab) ─────────────────────────────────────────

  const loadSales = async () => {
    setLoadingSales(true)
    try {
      const [ls, rs, ad, bh, bt] = await Promise.all([
        reportingService.getLostSales(lostSalesDays),
        reportingService.getRenewalSplit(),
        reportingService.getAvgDaysToPolicy(),
        reportingService.getRenewalBookHealth(lostSalesDays),
        reportingService.getRenewalBookTrend(6),
      ])
      setLostSales(ls)
      setRenewalSplit(rs)
      setAvgDays(ad)
      setBookHealth(bh)
      setBookTrend(bt)
    } finally {
      setLoadingSales(false)
    }
  }

  const loadOdDiscount = async () => {
    setLoadingOd(true)
    try {
      setOdDiscount(await reportingService.getOdDiscountUtilisation())
    } finally {
      setLoadingOd(false)
    }
  }

  const loadPipeline = async () => {
    setLoadingPipe(true)
    try {
      const [fc, cr] = await Promise.all([
        reportingService.getRenewalForecast(),
        reportingService.getCashPolicyRegister(),
      ])
      setForecast(fc)
      setCashRegister(cr)
    } finally {
      setLoadingPipe(false)
    }
  }

  const loadFraud = async () => {
    setLoadingFraud(true)
    try {
      const [fs, nc, fl] = await Promise.all([
        reportingService.getFraudSummary(),
        reportingService.getNcCoverage(),
        reportingService.getFraudLog(),
      ])
      setFraudSummary(fs)
      setNcCoverage(nc)
      setFraudLog(fl)
    } finally {
      setLoadingFraud(false)
    }
  }

  // Load on tab change (if data not yet loaded)
  useEffect(() => {
    if (tab === 'sales')       { if (!lostSales)    loadSales()      }
    if (tab === 'od-discount') { if (!odDiscount)   loadOdDiscount() }
    if (tab === 'pipeline')    { if (!forecast)     loadPipeline()   }
    if (tab === 'fraud')       { if (!fraudSummary) loadFraud()      }
    if (tab === 'lost-leads')  { if (!lostSummary)  loadLostLeads(1) }
    if (tab === 'telecaller-performance') { if (!tcStats)       loadTcStats()    }
  }, [tab]) // eslint-disable-line react-hooks/exhaustive-deps

  const loadTcStats = async () => {
    setLoadingAi(true)
    try {
      const [stats, scores] = await Promise.all([
        reportingService.getTelecallerPerformance(),
        aiExtractionEnabled ? reportingService.getTelecallerAIPerformance() : Promise.resolve(null),
      ])
      setTcStats(stats)
      setAiScores(scores)

      if (aiExtractionEnabled && scores) {
        const histories = await Promise.all(
          scores.map(s => reportingService.getTelecallerCoachingPlans(s.user_id).catch(() => []))
        )
        setCoachingPlanHistory(prev => {
          const next = { ...prev }
          scores.forEach((s, i) => { next[s.user_id] = histories[i] })
          return next
        })
      }
    } finally {
      setLoadingAi(false)
    }
  }

  // Re-fetch lost leads when filters change
  useEffect(() => {
    if (tab === 'lost-leads') loadLostLeads(1)
  }, [lostDays, lostFilterStatus, lostFilterDisp]) // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch lost sales when days filter changes
  useEffect(() => {
    if (tab === 'sales') loadSales()
  }, [lostSalesDays]) // eslint-disable-line react-hooks/exhaustive-deps

  const loadLostLeads = async (page = lostPage) => {
    setLoadingLost(true)
    try {
      const [summary, list] = await Promise.all([
        reportingService.getLostLeadsSummary(lostDays),
        reportingService.getLostLeadsList({
          days: lostDays,
          status: lostFilterStatus || undefined,
          disposition: lostFilterDisp || undefined,
          page,
          page_size: 50,
        }),
      ])
      setLostSummary(summary)
      setLostList(list.items)
      setLostTotal(list.total)
      setLostPage(page)
    } finally {
      setLoadingLost(false)
    }
  }

  const handleGenerateNarrative = async (leadId: number) => {
    setNarrativeLoading(leadId)
    try {
      const { narrative } = await reportingService.generateLostLeadNarrative(leadId)
      setNarrativeCache(prev => ({ ...prev, [leadId]: narrative }))
      // Update the item in-place too
      setLostList(prev => prev.map(item =>
        item.lead_id === leadId ? { ...item, ai_journey_summary: narrative } : item
      ))
    } finally {
      setNarrativeLoading(null)
    }
  }

  const handleGenerateCoachingPlan = async (userId: number) => {
    setCoachingPlanLoading(userId)
    try {
      const plan = await reportingService.generateTelecallerCoachingPlan(userId)
      setCoachingPlanHistory(prev => ({ ...prev, [userId]: [plan, ...(prev[userId] || [])] }))
    } finally {
      setCoachingPlanLoading(null)
    }
  }

  const handleLoadTcPerf = async (userId: number) => {
    if (tcPerfCache[userId] || tcPerfLoading === userId) return
    setTcPerfLoading(userId)
    try {
      const perf = await reportingService.getTelecallerLostPerformance(userId)
      setTcPerfCache(prev => ({ ...prev, [userId]: perf }))
    } catch { /* non-critical */ }
    finally { setTcPerfLoading(null) }
  }

  const handleExport = async () => {
    setExporting(true)
    try {
      await reportingService.downloadCallDetailExport(exportDays)
    } finally {
      setExporting(false)
    }
  }

  // ── OD discount chart data ────────────────────────────────────────────────

  const odChartData = odDiscount
    ? Object.entries(odDiscount).map(([slab, count]) => ({ slab, count }))
    : []

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="section-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <BarChart2 className="h-6 w-6" style={{ color: 'var(--brand-600)' }} />
            Reports
          </h1>
          <p className="page-subtitle">Operational analytics and data exports</p>
        </div>
      </div>

      {/* ── Tab bar ─────────────────────────────────────────────────────── */}
      {isMobile ? (
        // Mobile: a single-row dropdown instead of a horizontally-scrolling
        // strip of tabs — avoids making the user scroll just to see/reach
        // every report tab on a narrow screen.
        <div style={{ position: 'relative' }}>
          <select
            className="input"
            value={tab}
            onChange={e => setTab(e.target.value as Tab)}
            style={{
              width: '100%',
              fontWeight: 600,
              appearance: 'none',
              paddingRight: 36,
            }}
          >
            {TABS.map(({ key, label }) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
          <ChevronDown
            className="h-4 w-4"
            style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-tertiary)' }}
          />
        </div>
      ) : (
        <div className="flex gap-1 overflow-x-auto"
          style={{ borderBottom: '1px solid var(--border-default)' }}>
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              className="px-4 py-2.5 text-sm font-medium border-b-2 -mb-px flex items-center gap-2 shrink-0 transition-colors"
              style={{
                borderBottomColor: tab === key ? 'var(--brand-primary)' : 'transparent',
                color: tab === key ? 'var(--brand-primary)' : 'var(--text-tertiary)',
              }}
              onClick={() => setTab(key)}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          ))}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          Tab: Sales Analysis
      ══════════════════════════════════════════════════════════════════ */}
      {tab === 'sales' && (
        <div className="space-y-6">

          {/* Renewal book health KPIs — retention rate & premium retained replace
              raw renewal/new-business counts; this is a renewal-driven book, so
              the headline question is "how much of it did we keep", not composition. */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider mb-3"
              style={{ color: 'var(--text-tertiary)' }}>
              Renewal Book Health
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <KpiTile
                label="Renewal Retention Rate"
                value={bookHealth?.retention_rate != null ? `${bookHealth.retention_rate}%` : '—'}
                accent="var(--brand-600)"
                accentBg="var(--brand-primary-subtle)"
                icon={Target}
                sub={bookHealth
                  ? `of leads due this period → policy issued (${bookHealth.renewal_issued_count} of ${bookHealth.renewal_finalized_count}, last ${bookHealth.period_days}d)`
                  : undefined}
              />
              <KpiTile
                label="Renewal Premium Retained"
                value={bookHealth ? fmt(bookHealth.premium_retained) : '—'}
                accent="var(--success-text)"
                accentBg="var(--success-bg)"
                icon={IndianRupee}
                sub={bookHealth ? `vs ${fmt(bookHealth.premium_due)} total book due this period` : undefined}
              />
              <KpiTile
                label="At-Risk Renewals (30d)"
                value={bookHealth ? bookHealth.at_risk_count.toLocaleString() : '—'}
                accent="var(--warning-text)"
                accentBg="var(--warning-bg)"
                icon={AlertTriangle}
                sub={bookHealth ? `${fmt(bookHealth.at_risk_premium)} premium · not yet quoted` : undefined}
              />
              <KpiTile
                label="Avg Days Lead → Policy"
                value={avgDays ? `${avgDays.average_days} days` : '—'}
                accent="var(--info-text)"
                accentBg="var(--info-bg)"
                icon={Clock}
                sub="unchanged — kept from today's report"
              />
            </div>
          </div>

          {/* Renewal book trend + new-business share */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-tertiary)' }}>
              Renewal Book Trend
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="card lg:col-span-2">
              <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                Policies issued by month <span className="font-normal text-xs" style={{ color: 'var(--text-tertiary)' }}>(month-on-month)</span>
              </p>
              <p className="text-xs mt-0.5 mb-3" style={{ color: 'var(--text-tertiary)' }}>
                Renewal is the primary series, new business a thin secondary line — not an
                equal-weight split. Trend stays monthly; retention rate above switches to
                year-over-year once 12+ months of history exist, since each policy is an annual
                cycle and MoM comparisons would compare unrelated renewal cohorts.
              </p>
              {!bookTrend || bookTrend.length === 0 ? (
                <p className="text-sm py-8 text-center" style={{ color: 'var(--text-tertiary)' }}>
                  No data
                </p>
              ) : (
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={bookTrend}>
                      <defs>
                        <linearGradient id="renewalTrendFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="var(--brand-primary)" stopOpacity={0.28} />
                          <stop offset="100%" stopColor="var(--brand-primary)" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default)" vertical={false} />
                      <XAxis dataKey="month" stroke="var(--text-tertiary)"
                        tick={{ fontSize: chartTickSize, fill: 'var(--text-secondary)' }}
                        axisLine={false} tickLine={false} />
                      <YAxis stroke="var(--text-tertiary)"
                        tick={{ fontSize: chartTickSize, fill: 'var(--text-tertiary)' }}
                        axisLine={false} tickLine={false} width={32} />
                      <Tooltip cursor={{ fill: 'var(--surface-hover)' }}
                        contentStyle={{ background: 'var(--surface-elevated)', border: '1px solid var(--border-default)', borderRadius: 8, fontSize: 12 }} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Area type="monotone" dataKey="renewal" name="Renewal" stroke="var(--brand-primary)"
                        strokeWidth={3} fill="url(#renewalTrendFill)" dot={{ r: 3, fill: 'var(--brand-primary)' }} />
                      <Line type="monotone" dataKey="new_business" name="New Business" stroke="var(--text-tertiary)" strokeWidth={1.5} strokeDasharray="4 3" dot={{ r: 2 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
            <div className="card flex flex-col items-center justify-center text-center">
              <p className="text-sm font-semibold mb-0.5" style={{ color: 'var(--text-primary)' }}>
                New Business Share
              </p>
              <p className="text-xs mb-4" style={{ color: 'var(--text-tertiary)' }}>
                De-emphasized — supplementary, not the headline story
              </p>
              {(() => {
                const pct = renewalSplit && renewalSplit.total > 0
                  ? (renewalSplit.new_business / renewalSplit.total) * 100
                  : 0
                const r = 15.5
                const circumference = 2 * Math.PI * r
                return (
                  <div style={{ position: 'relative', width: 96, height: 96 }}>
                    <svg viewBox="0 0 36 36" style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
                      <circle cx="18" cy="18" r={r} fill="none" stroke="var(--surface-subtle)" strokeWidth="4" />
                      <circle cx="18" cy="18" r={r} fill="none" stroke="var(--text-tertiary)" strokeWidth="4"
                        strokeDasharray={`${(pct / 100) * circumference} ${circumference}`}
                        strokeLinecap="round" />
                    </svg>
                    <div style={{
                      position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 20, fontWeight: 700, color: 'var(--text-secondary)',
                    }}>
                      {renewalSplit ? `${Math.round(pct)}%` : '—'}
                    </div>
                  </div>
                )
              })()}
              <p className="text-xs mt-3" style={{ color: 'var(--text-tertiary)' }}>
                {renewalSplit ? `${renewalSplit.new_business} of ${renewalSplit.total} policies issued` : ''}
              </p>
            </div>
            </div>
          </div>

          {/* Upcoming renewal pipeline — strictly forward-looking; overdue lives
              next to Lost Sales Analysis below, since it's already missed, not upcoming */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-tertiary)' }}>
              Upcoming Renewal Pipeline
            </p>
            <div className="card">
              <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                Renewal book due, by urgency
              </p>
              <p className="text-xs mt-0.5 mb-3" style={{ color: 'var(--text-tertiary)' }}>
                Strictly forward-looking — overdue renewals moved out of this widget (see Lost
                Sales Analysis below) since "overdue" isn't upcoming, it's already missed
              </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-lg p-3" style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning-text)' }}>
                <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Due ≤ 7 days</p>
                <p className="text-lg font-bold" style={{ color: 'var(--warning-text)' }}>
                  {bookHealth?.pipeline.due_7.count ?? '—'}
                </p>
                <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                  {bookHealth ? fmt(bookHealth.pipeline.due_7.premium) : ''}
                </p>
              </div>
              <div className="rounded-lg p-3" style={{ background: 'var(--surface-subtle)' }}>
                <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Due ≤ 30 days</p>
                <p className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
                  {bookHealth?.pipeline.due_30.count ?? '—'}
                </p>
                <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                  {bookHealth ? fmt(bookHealth.pipeline.due_30.premium) : ''}
                </p>
              </div>
              <div className="rounded-lg p-3" style={{ background: 'var(--surface-subtle)' }}>
                <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Due ≤ 60 days</p>
                <p className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
                  {bookHealth?.pipeline.due_60.count ?? '—'}
                </p>
                <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                  {bookHealth ? fmt(bookHealth.pipeline.due_60.premium) : ''}
                </p>
              </div>
            </div>
          </div>
          </div>

          {/* Lost Sales Analysis */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-tertiary)' }}>
              Lost Sales Analysis
            </p>
          <div className="card space-y-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                  By reason &amp; by agent
                </p>
                <button onClick={loadSales} disabled={loadingSales} className="btn-icon" title="Refresh">
                  <RefreshCw className={`h-3.5 w-3.5 ${loadingSales ? 'animate-spin' : ''}`} />
                </button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Period:</span>
                {[30, 60, 90].map(d => (
                  <button
                    key={d}
                    onClick={() => setLostSalesDays(d)}
                    className={lostSalesDays === d ? 'chip active' : 'chip'}
                  >
                    {d}d
                  </button>
                ))}
              </div>
            </div>
            <p className="text-xs -mt-2" style={{ color: 'var(--text-tertiary)' }}>
              Confirmed losses only — leads with an explicit LOST/NOT_RESPONSIVE disposition and
              reason. Unchanged from today.
            </p>

            {bookHealth != null && bookHealth.overdue_count > 0 && (
              <div className="flex items-center gap-3 rounded-lg p-3"
                style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger-text)' }}>
                <span className="text-lg font-bold" style={{ color: 'var(--danger-text)' }}>
                  {bookHealth.overdue_count}
                </span>
                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                  renewal{bookHealth.overdue_count !== 1 ? 's' : ''} overdue
                  ({fmt(bookHealth.overdue_premium)} premium) — not yet re-engaged past due date.
                  Distinct from confirmed losses below: no disposition logged yet, so not counted
                  in the bars — a silent-churn risk to chase before it becomes one.
                </span>
              </div>
            )}

            {loadingSales ? (
              <div className="h-48 flex items-center justify-center">
                <div className="spinner" />
              </div>
            ) : !lostSales || lostSales.by_reason.length === 0 ? (
              <p className="text-sm py-8 text-center" style={{ color: 'var(--text-tertiary)' }}>
                No lost sales in this period
              </p>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* By reason — horizontal bar */}
                <div>
                  <p className="text-xs font-medium mb-3" style={{ color: 'var(--text-secondary)' }}>
                    By Reason
                  </p>
                  <div className="h-52">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={lostSales.by_reason}
                        layout="vertical"
                        barSize={14}
                        margin={{ left: 8, right: 16, top: 0, bottom: 0 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke="var(--border-default)"
                          horizontal={false}
                        />
                        <XAxis
                          type="number"
                          stroke="var(--text-tertiary)"
                          tick={{ fontSize: chartTickSize, fill: 'var(--text-tertiary)' }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis
                          type="category"
                          dataKey="reason"
                          width={isMobile ? 100 : 160}
                          tick={{ fontSize: chartTickSize, fill: 'var(--text-secondary)' }}
                          axisLine={false}
                          tickLine={false}
                          tickFormatter={v =>
                            v.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
                          }
                        />
                        <Tooltip
                          cursor={{ fill: 'var(--surface-hover)' }}
                          contentStyle={{
                            background: 'var(--surface-card)',
                            border: '1px solid var(--border-default)',
                            borderRadius: 6,
                            fontSize: 12,
                            color: 'var(--text-primary)',
                          }}
                        />
                        <Bar dataKey="count" fill="var(--danger-text)" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* By telecaller */}
                <div>
                  <p className="text-xs font-medium mb-3" style={{ color: 'var(--text-secondary)' }}>
                    By Agent (Not Interested / Booked Elsewhere)
                  </p>
                  {lostSales.by_telecaller.length === 0 ? (
                    <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>No data</p>
                  ) : (
                    <div className="space-y-2">
                      {lostSales.by_telecaller
                        .sort((a, b) => b.lost_count - a.lost_count)
                        .map(tc => {
                          const max = lostSales.by_telecaller[0]?.lost_count || 1
                          const pct = Math.round((tc.lost_count / max) * 100)
                          return (
                            <div key={tc.name} className="space-y-0.5">
                              <div className="flex items-center justify-between text-xs">
                                <span style={{ color: 'var(--text-primary)' }}>{tc.name}</span>
                                <span className="font-semibold tabular-nums"
                                  style={{ color: 'var(--danger-text)' }}>
                                  {tc.lost_count}
                                </span>
                              </div>
                              <div className="h-1.5 rounded-full overflow-hidden"
                                style={{ background: 'var(--surface-subtle)' }}>
                                <div
                                  className="h-full rounded-full"
                                  style={{ width: `${pct}%`, background: 'var(--danger-text)' }}
                                />
                              </div>
                            </div>
                          )
                        })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          Tab: OD Discount Utilisation
      ══════════════════════════════════════════════════════════════════ */}
      {tab === 'od-discount' && (
        <div className="card space-y-4">
          <SectionHeader
            title="OD Discount Utilisation"
            onRefresh={loadOdDiscount}
            loading={loadingOd}
          />
          <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
            Distribution of OD discount % across all issued policies.
            Policies above 30% require Insurance Head approval.
          </p>

          {loadingOd ? (
            <div className="h-64 flex items-center justify-center">
              <div className="spinner" />
            </div>
          ) : !odDiscount ? (
            <p className="text-sm py-8 text-center" style={{ color: 'var(--text-tertiary)' }}>
              No data
            </p>
          ) : (
            <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={odChartData} barSize={chartBarSize}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="var(--border-default)"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="slab"
                      stroke="var(--text-tertiary)"
                      tick={{ fontSize: chartTickSize, fill: 'var(--text-secondary)' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={v => `${v}%`}
                    />
                    <YAxis
                      stroke="var(--text-tertiary)"
                      tick={{ fontSize: chartTickSize, fill: 'var(--text-tertiary)' }}
                      axisLine={false}
                      tickLine={false}
                      width={32}
                    />
                    <Tooltip
                      cursor={{ fill: 'var(--surface-hover)' }}
                      contentStyle={{
                        background: 'var(--surface-card)',
                        border: '1px solid var(--border-default)',
                        borderRadius: 6,
                        fontSize: 12,
                        color: 'var(--text-primary)',
                      }}
                      formatter={(v: number) => [v, 'Policies']}
                      labelFormatter={(l: string) => `Discount: ${l}%`}
                    />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                      {odChartData.map(({ slab }) => {
                        const slabNum = parseFloat(slab)
                        const isAbove30 = !isNaN(slabNum) && slabNum > 30
                        const isHighSlabs = slab === '31-50' || slab === '50+'
                        return (
                          <Cell
                            key={slab}
                            fill={isHighSlabs || isAbove30
                              ? 'var(--warning-text)'
                              : 'var(--brand-primary)'}
                          />
                        )
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          Tab: Pipeline
      ══════════════════════════════════════════════════════════════════ */}
      {tab === 'pipeline' && (
        <div className="space-y-6">

          {/* Renewal Forecast */}
          <div className="card space-y-4">
            <SectionHeader
              title="Renewal Forecast"
              onRefresh={loadPipeline}
              loading={loadingPipe}
            />
            {loadingPipe ? (
              <div className="h-24 flex items-center justify-center">
                <div className="spinner" />
              </div>
            ) : !forecast ? (
              <p className="text-sm py-6 text-center" style={{ color: 'var(--text-tertiary)' }}>
                No data
              </p>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {[
                    { label: 'Overdue',       value: forecast.overdue, accent: 'var(--danger-text)',  bg: 'var(--danger-bg)'  },
                    { label: 'Due ≤ 7 days',  value: forecast.due_7,   accent: 'var(--warning-text)', bg: 'var(--warning-bg)' },
                    { label: 'Due ≤ 30 days', value: forecast.due_30,  accent: 'var(--orange-text)',  bg: 'var(--orange-bg)'  },
                    { label: 'Due ≤ 60 days', value: forecast.due_60,  accent: 'var(--info-text)',    bg: 'var(--info-bg)'    },
                    {
                      label: 'Total Premium',
                      value: fmt(forecast.total_premium),
                      accent: 'var(--success-text)',
                      bg:     'var(--success-bg)',
                    },
                  ].map(({ label, value, accent, bg }) => (
                    <div key={label} className="rounded-lg px-4 py-3 space-y-1" style={{ background: bg }}>
                      <p className="text-xs" style={{ color: accent }}>{label}</p>
                      <p className="text-xl font-bold tabular-nums" style={{ color: accent }}>
                        {value}
                      </p>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Cash Policy Register */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <SectionHeader title="Cash Policy Register" />
              {cashRegister && (
                <p className="text-sm font-semibold" style={{ color: 'var(--success-text)' }}>
                  Total: {fmt(cashRegister.total_cash_collected)}
                </p>
              )}
            </div>

            {loadingPipe ? (
              <div className="h-24 flex items-center justify-center">
                <div className="spinner" />
              </div>
            ) : !cashRegister || cashRegister.policies.length === 0 ? (
              <div className="card py-8 text-center text-sm"
                style={{ color: 'var(--text-tertiary)' }}>
                No cash policies recorded
              </div>
            ) : (
              <div className="table-card">
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Lead</th>
                        <th>Customer</th>
                        <th>Policy Number</th>
                        <th className="text-right">Premium</th>
                        <th>Payment Ref</th>
                        <th>Issued At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cashRegister.policies.map(p => (
                        <tr key={p.lead_id}>
                          <td>
                            <a
                              href={`/leads/${p.lead_id}`}
                              className="font-mono text-xs hover:underline"
                              style={{ color: 'var(--brand-primary)' }}
                            >
                              #{p.lead_id}
                            </a>
                          </td>
                          <td className="text-primary-data">{p.customer_name}</td>
                          <td className="font-mono text-xs">{p.policy_number || '—'}</td>
                          <td className="text-right tabular-nums font-medium">
                            {fmt(p.premium_amount)}
                          </td>
                          <td className="font-mono text-xs"
                            style={{ color: 'var(--text-secondary)' }}>
                            {p.payment_reference || '—'}
                          </td>
                          <td style={{ color: 'var(--text-tertiary)' }}>
                            {fmtDate(p.policy_issued_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          Tab: Fraud & Compliance
      ══════════════════════════════════════════════════════════════════ */}
      {tab === 'fraud' && (
        <div className="space-y-6">

          {/* Fraud Summary + NC Coverage side by side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            {/* Fraud Summary */}
            <div className="card space-y-4">
              <SectionHeader
                title="Fraud Summary"
                onRefresh={loadFraud}
                loading={loadingFraud}
              />
              {loadingFraud ? (
                <div className="h-24 flex items-center justify-center"><div className="spinner" /></div>
              ) : !fraudSummary ? null : (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    { label: 'Total Alerts',  value: fraudSummary.total_alerts,  accent: 'var(--text-primary)',  bg: 'var(--surface-subtle)' },
                    { label: 'Open',          value: fraudSummary.open_alerts,   accent: 'var(--warning-text)', bg: 'var(--warning-bg)'     },
                    { label: 'Critical Open', value: fraudSummary.critical_open, accent: 'var(--danger-text)',  bg: 'var(--danger-bg)'      },
                  ].map(({ label, value, accent, bg }) => (
                    <div key={label} className="rounded-lg px-3 py-3 text-center"
                      style={{ background: bg }}>
                      <p className="text-2xl font-bold tabular-nums" style={{ color: accent }}>
                        {value}
                      </p>
                      <p className="text-xs mt-0.5" style={{ color: accent }}>{label}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* NC Coverage */}
            <div className="card space-y-4">
              <SectionHeader title="Negative Confirmation Coverage" />
              {loadingFraud ? (
                <div className="h-24 flex items-center justify-center"><div className="spinner" /></div>
              ) : !ncCoverage ? null : (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {[
                      { label: 'Total Lost',  value: ncCoverage.total_lost,  accent: 'var(--text-primary)',  bg: 'var(--surface-subtle)' },
                      { label: 'Covered',     value: ncCoverage.covered,     accent: 'var(--success-text)', bg: 'var(--success-bg)'     },
                      { label: 'Coverage %',  value: `${ncCoverage.rate}%`,  accent: ncCoverage.rate >= 80 ? 'var(--success-text)' : 'var(--warning-text)',
                        bg: ncCoverage.rate >= 80 ? 'var(--success-bg)' : 'var(--warning-bg)' },
                    ].map(({ label, value, accent, bg }) => (
                      <div key={label} className="rounded-lg px-3 py-3 text-center"
                        style={{ background: bg }}>
                        <p className="text-2xl font-bold tabular-nums" style={{ color: accent }}>
                          {value}
                        </p>
                        <p className="text-xs mt-0.5" style={{ color: accent }}>{label}</p>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div className="h-2 rounded-full overflow-hidden"
                      style={{ background: 'var(--surface-subtle)' }}>
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${Math.min(ncCoverage.rate, 100)}%`,
                          background: ncCoverage.rate >= 80
                            ? 'var(--success-text)'
                            : 'var(--warning-text)',
                        }}
                      />
                    </div>
                    <p className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                      DPDP target: 80% coverage of lost leads
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Fraud Log */}
          <div>
            <SectionHeader title={`Fraud Log${fraudLog ? ` (${fraudLog.length})` : ''}`} />
            {loadingFraud ? (
              <div className="h-24 flex items-center justify-center"><div className="spinner" /></div>
            ) : !fraudLog || fraudLog.length === 0 ? (
              <div className="card py-8 text-center text-sm"
                style={{ color: 'var(--text-tertiary)' }}>
                No fraud alerts recorded
              </div>
            ) : (
              <div className="table-card">
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Type</th>
                        <th>Severity</th>
                        <th>Status</th>
                        <th>Lead</th>
                        <th>Description</th>
                        <th>Created</th>
                        <th>Resolved</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fraudLog.map(a => (
                        <tr key={a.id}>
                          <td className="font-mono text-xs">{a.id}</td>
                          <td className="text-primary-data text-xs">
                            {a.alert_type.replace(/_/g, ' ')}
                          </td>
                          <td>
                            <span className={SEVERITY_BADGE[a.severity] || 'badge-neutral'}>
                              {a.severity}
                            </span>
                          </td>
                          <td>
                            <span className={ALERT_STATUS_BADGE[a.status] || 'badge-neutral'}>
                              {a.status.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td>
                            {a.lead_id ? (
                              <a
                                href={`/leads/${a.lead_id}`}
                                className="font-mono text-xs hover:underline"
                                style={{ color: 'var(--brand-primary)' }}
                              >
                                #{a.lead_id}
                              </a>
                            ) : '—'}
                          </td>
                          <td className="text-xs max-w-xs truncate" title={a.description || '—'}
                            style={{ color: 'var(--text-secondary)' }}>
                            {a.description || '—'}
                          </td>
                          <td className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                            {fmtDate(a.created_at)}
                          </td>
                          <td className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                            {fmtDate(a.resolved_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          Tab: Lost Leads
      ══════════════════════════════════════════════════════════════════ */}
      {tab === 'lost-leads' && (
        <div className="space-y-6">

          {/* Filter bar */}
          <div className="flex flex-wrap gap-2 items-center">
            <div className="flex items-center gap-2">
              <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Period:</span>
              {[7, 30, 90].map(d => (
                <button key={d} onClick={() => setLostDays(d)} className={lostDays === d ? 'chip active' : 'chip'}>
                  {d}d
                </button>
              ))}
            </div>
            <button onClick={() => loadLostLeads(1)} disabled={loadingLost} className="btn-icon" title="Refresh" style={{ marginLeft: isMobile ? 'auto' : 0 }}>
              <RefreshCw className={`h-3.5 w-3.5 ${loadingLost ? 'animate-spin' : ''}`} />
            </button>
            <select
              className="input text-sm"
              style={{ flex: isMobile ? '1 1 45%' : '0 1 auto', width: isMobile ? undefined : 'auto', minWidth: isMobile ? 0 : 160 }}
              value={lostFilterStatus}
              onChange={e => setLostFilterStatus(e.target.value)}
            >
              <option value="">All statuses</option>
              <option value="LOST">Lost</option>
              <option value="LOST_RENEWED_ELSEWHERE">Renewed Elsewhere</option>
              <option value="NOT_RESPONSIVE">Not Responsive</option>
            </select>
            <select
              className="input text-sm"
              style={{ flex: isMobile ? '1 1 45%' : '0 1 auto', width: isMobile ? undefined : 'auto', minWidth: isMobile ? 0 : 180 }}
              value={lostFilterDisp}
              onChange={e => setLostFilterDisp(e.target.value)}
            >
              <option value="">All loss reasons</option>
              <option value="NOT_INTERESTED">Not Interested</option>
              <option value="CUSTOMER_BOOKED_ELSEWHERE">Booked Elsewhere</option>
              <option value="SOLD_VEHICLE">Sold Vehicle</option>
              <option value="DUE_DATE_LAPSED">Due Date Lapsed</option>
              <option value="NO_ANSWER">No Answer</option>
              <option value="WRONG_NUMBER">Wrong Number</option>
            </select>
          </div>

          {/* KPI cards */}
          {lostSummary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <KpiTile label="Total Lost" value={lostSummary.total_lost} accent="var(--danger-text)" accentBg="var(--danger-bg)" icon={XCircle} />
              {lostSummary.by_status.map(s => (
                <KpiTile
                  key={s.status}
                  label={s.status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  value={s.count}
                  accent={s.status === 'LOST' ? 'var(--danger-text)' : s.status === 'LOST_RENEWED_ELSEWHERE' ? 'var(--warning-text)' : 'var(--text-secondary)'}
                  accentBg={s.status === 'LOST' ? 'var(--danger-bg)' : s.status === 'LOST_RENEWED_ELSEWHERE' ? 'var(--warning-bg)' : 'var(--surface-subtle)'}
                  icon={TrendingDown}
                />
              ))}
            </div>
          )}

          {/* Charts row */}
          {lostSummary && lostSummary.total_lost > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

              {/* Loss reason bar chart */}
              <div className="card space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>
                  Loss Reason (Last Call Disposition)
                </p>
                <div className="h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={lostSummary.by_disposition} layout="vertical" barSize={14} margin={{ left: 8, right: 16, top: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default)" horizontal={false} />
                      <XAxis type="number" stroke="var(--text-tertiary)" tick={{ fontSize: chartTickSize, fill: 'var(--text-tertiary)' }} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="disposition" width={isMobile ? 110 : 170}
                        tick={{ fontSize: chartTickSize, fill: 'var(--text-secondary)' }} axisLine={false} tickLine={false}
                        tickFormatter={v => v.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())} />
                      <Tooltip cursor={{ fill: 'var(--surface-hover)' }}
                        contentStyle={{ background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 12, color: 'var(--text-primary)' }} />
                      <Bar dataKey="count" fill="var(--danger-text)" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* By telecaller bar chart */}
              <div className="card space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>
                  Lost Leads by Agent
                </p>
                <div className="space-y-2">
                  {lostSummary.by_telecaller.slice(0, 8).map(tc => {
                    const max = lostSummary.by_telecaller[0]?.lost_count || 1
                    const pct = Math.round((tc.lost_count / max) * 100)
                    return (
                      <div key={tc.name} className="space-y-0.5">
                        <div className="flex items-center justify-between text-xs">
                          <span style={{ color: 'var(--text-primary)' }}>{tc.name}</span>
                          <span className="font-semibold tabular-nums" style={{ color: 'var(--danger-text)' }}>{tc.lost_count}</span>
                        </div>
                        <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--surface-subtle)' }}>
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: 'var(--danger-text)' }} />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Trend sparkline */}
          {lostSummary && lostSummary.trend.length > 1 && (
            <div className="card space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>
                Daily Lost Lead Trend
              </p>
              <div className="h-36">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={lostSummary.trend} barSize={isMobile ? 6 : 10}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default)" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--text-tertiary)' }} axisLine={false} tickLine={false}
                      tickFormatter={v => v.slice(5)} />
                    <YAxis tick={{ fontSize: 10, fill: 'var(--text-tertiary)' }} axisLine={false} tickLine={false} width={24} allowDecimals={false} />
                    <Tooltip cursor={{ fill: 'var(--surface-hover)' }}
                      contentStyle={{ background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 12, color: 'var(--text-primary)' }}
                      labelFormatter={v => `Date: ${v}`} formatter={(val: number) => [val, 'Lost']} />
                    <Bar dataKey="count" fill="var(--danger-text)" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Lead table with expandable rows */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>
                Lost Leads — {lostTotal} total
              </p>
            </div>

            {loadingLost ? (
              <div className="card h-40 flex items-center justify-center"><div className="spinner" /></div>
            ) : lostList.length === 0 ? (
              <div className="card py-10 text-center text-sm" style={{ color: 'var(--text-tertiary)' }}>
                No lost leads in this period
              </div>
            ) : isMobile ? (
              /* ── Mobile card list ── */
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                {lostList.map((item, idx) => {
                  const expanded = expandedLead === item.lead_id
                  const narrative = narrativeCache[item.lead_id] ?? item.ai_journey_summary
                  const tcPerf = tcPerfCache[item.lead_id]
                  const maxReasonCount = tcPerf?.loss_reasons[0]?.count || 1
                  const BAR_COLORS = ['#ef4444','#f97316','#a855f7','#3b82f6']
                  return (
                    <div key={item.lead_id} style={{ borderBottom: idx < lostList.length - 1 ? '1px solid var(--border-default)' : undefined }}>
                      {/* collapsed row */}
                      <div
                        onClick={() => {
                          const next = expanded ? null : item.lead_id
                          setExpandedLead(next)
                          if (next !== null) handleLoadTcPerf(item.lead_id)
                        }}
                        style={{ display: 'grid', gridTemplateColumns: '20px 1fr auto', gap: 10, padding: '10px 12px', cursor: 'pointer', alignItems: 'flex-start' }}
                      >
                        <div style={{ paddingTop: 2 }}>
                          {expanded
                            ? <ChevronDown className="h-3.5 w-3.5" style={{ color: 'var(--brand-primary)' }} />
                            : <ChevronRight className="h-3.5 w-3.5" style={{ color: 'var(--text-tertiary)' }} />}
                        </div>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                            <span className="font-mono text-xs" style={{ color: 'var(--brand-primary)' }}>#{item.lead_id}</span>
                            <span className={STATUS_BADGE[item.status] || 'badge-neutral'} style={{ fontSize: '0.6rem', padding: '1px 5px' }}>
                              {item.status.replace(/_/g, ' ')}
                            </span>
                          </div>
                          <div className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>{item.customer_name}</div>
                          {item.vehicle_reg && (
                            <div className="font-mono text-xs" style={{ color: 'var(--text-secondary)', marginTop: 1 }}>{item.vehicle_reg}</div>
                          )}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 5 }}>
                            {item.disposition_sequence.filter(d => d !== 'NONE').map((d, i) => (
                              <span key={i} className={DISPOSITION_BADGE[d] || 'badge-neutral'}
                                style={{ fontSize: '0.6rem', padding: '1px 5px' }}>
                                {d.replace(/_/g, ' ')}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div className="tabular-nums font-medium text-sm" style={{ color: item.days_to_lost && item.days_to_lost > 14 ? 'var(--danger-text)' : 'var(--text-secondary)' }}>
                            {item.days_to_lost !== null ? `${item.days_to_lost}d` : '—'}
                          </div>
                          <div className="text-xs" style={{ color: 'var(--text-tertiary)', marginTop: 2 }}>{fmtDate(item.status_changed_at)}</div>
                          <div className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{item.assigned_to_name}</div>
                        </div>
                      </div>

                      {/* mobile expanded panel — single column */}
                      {expanded && (
                        <div style={{ background: 'var(--surface-subtle)', borderTop: '1px solid var(--border-default)', padding: '14px 14px 16px' }}>
                          {/* Disposition Journey */}
                          <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-tertiary)' }}>
                            Disposition Journey
                          </p>
                          {item.all_remarks.length === 0 ? (
                            <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>No calls recorded</p>
                          ) : (
                            <div>
                              {item.all_remarks.map((r, ridx) => {
                                const isLast = ridx === item.all_remarks.length - 1
                                return (
                                  <div key={r.call_no} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, width: 22 }}>
                                      <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--brand-primary-subtle)', color: 'var(--brand-600)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700 }}>
                                        {r.call_no}
                                      </div>
                                      {!isLast && <div style={{ width: 2, flex: 1, minHeight: 12, background: 'var(--border-default)', borderRadius: 2, margin: '3px 0' }} />}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0, paddingBottom: isLast ? 0 : 10 }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                        <span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>{fmtDateTime(r.date)}</span>
                                        {r.disposition && r.disposition !== 'NONE' && (
                                          <span className={DISPOSITION_BADGE[r.disposition] || 'badge-neutral'} style={{ fontSize: '0.6rem', padding: '1px 5px' }}>
                                            {r.disposition.replace(/_/g, ' ')}
                                          </span>
                                        )}
                                      </div>
                                      {r.remarks && <p style={{ fontSize: 11, marginTop: 3, color: 'var(--text-primary)', lineHeight: 1.4 }}>{r.remarks}</p>}
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          )}

                          {/* AI summary */}
                          {aiExtractionEnabled && (
                            <div style={{ border: '1px solid var(--border-default)', borderRadius: 8, padding: '8px 10px', marginTop: 10, background: 'var(--surface-card)' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}>
                                <Sparkles className="h-3 w-3" style={{ color: 'var(--brand-600)' }} />
                                <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)', fontSize: '0.6rem' }}>AI Journey Summary</p>
                              </div>
                              {narrative ? (
                                <p style={{ fontSize: 11, color: 'var(--text-primary)', lineHeight: 1.5 }}>{narrative}</p>
                              ) : narrativeLoading === item.lead_id ? (
                                <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-tertiary)' }}>
                                  <div className="spinner" style={{ width: 10, height: 10 }} /> Generating…
                                </div>
                              ) : aiOnDemand ? (
                                <button className="btn-secondary flex items-center gap-1.5" style={{ fontSize: 11 }}
                                  onClick={e => { e.stopPropagation(); handleGenerateNarrative(item.lead_id) }}>
                                  <Sparkles className="h-3 w-3" /> Generate AI Summary
                                </button>
                              ) : (
                                <p style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Summary will be available shortly…</p>
                              )}
                            </div>
                          )}

                          <a href={`/leads/${item.lead_id}`} onClick={e => e.stopPropagation()}
                            style={{ fontSize: 11, color: 'var(--brand-primary)', display: 'block', marginTop: 8 }}>
                            View Full Lead →
                          </a>

                          {/* TC Performance */}
                          <div style={{ borderTop: '1px solid var(--border-default)', marginTop: 12, paddingTop: 12 }}>
                            <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-tertiary)' }}>
                              Telecaller Performance
                            </p>
                            {tcPerfLoading === item.lead_id ? (
                              <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-tertiary)' }}>
                                <div className="spinner" style={{ width: 10, height: 10 }} /> Loading…
                              </div>
                            ) : tcPerf ? (
                              <div className="card" style={{ padding: '10px 12px' }}>
                                {/* avatar + name */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--brand-primary-subtle)', color: 'var(--brand-600)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                                    {tcPerf.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                                  </div>
                                  <div>
                                    <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{tcPerf.name}</p>
                                    <p style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>Telecaller · MTD</p>
                                  </div>
                                </div>
                                {/* ── This lead ── */}
                                <p style={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-tertiary)', marginBottom: 5 }}>This lead</p>
                                {[
                                  { label: 'Calls made', value: String(item.total_calls) },
                                  { label: 'Days to lost', value: item.days_to_lost !== null ? `${item.days_to_lost}d` : '—' },
                                  { label: 'First contact', value: fmtDate(item.first_contacted_at) },
                                  { label: 'Last contact', value: fmtDate(item.last_contacted_at) },
                                  { label: 'Max discount offered', value: item.max_discount_offered !== null ? `${item.max_discount_offered}%` : '—' },
                                  { label: 'Final disposition', value: item.disposition_sequence.filter(d => d !== 'NONE').slice(-1)[0]?.replace(/_/g, ' ') ?? '—' },
                                ].map(row => (
                                  <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid var(--border-default)', fontSize: 11 }}>
                                    <span style={{ color: 'var(--text-tertiary)' }}>{row.label}</span>
                                    <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{row.value}</span>
                                  </div>
                                ))}

                                {/* ── MTD ── */}
                                <p style={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-tertiary)', marginTop: 10, marginBottom: 5 }}>Month to date</p>
                                {/* 3-up KPI tiles */}
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginBottom: 8 }}>
                                  {[
                                    { label: 'Assigned', value: tcPerf.assigned_mtd, color: undefined },
                                    { label: 'Policies', value: tcPerf.policies_mtd, color: 'var(--success-text)' },
                                    { label: 'Lost', value: tcPerf.lost_mtd, color: 'var(--danger-text)' },
                                  ].map(kpi => (
                                    <div key={kpi.label} style={{ background: 'var(--surface-page)', borderRadius: 6, padding: '6px 8px', textAlign: 'center' }}>
                                      <div style={{ fontSize: 16, fontWeight: 700, color: kpi.color ?? 'var(--text-primary)' }}>{kpi.value}</div>
                                      <div style={{ fontSize: 9, color: 'var(--text-tertiary)', marginTop: 2 }}>{kpi.label}</div>
                                    </div>
                                  ))}
                                </div>
                                {/* aux rows */}
                                {[
                                  { label: 'Conversion rate', value: `${tcPerf.conversion_rate}%`, color: tcPerf.conversion_rate < 20 ? 'var(--warning-text)' : 'var(--success-text)' },
                                  { label: 'Avg calls / lost lead', value: tcPerf.avg_calls_per_lost !== null ? String(tcPerf.avg_calls_per_lost) : '—', color: undefined },
                                  { label: 'Avg days to close', value: tcPerf.avg_days_to_lost !== null ? `${tcPerf.avg_days_to_lost}d` : '—', color: undefined },
                                ].map(row => (
                                  <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid var(--border-default)', fontSize: 11 }}>
                                    <span style={{ color: 'var(--text-tertiary)' }}>{row.label}</span>
                                    <span style={{ fontWeight: 700, color: row.color ?? 'var(--text-primary)' }}>{row.value}</span>
                                  </div>
                                ))}
                                {/* loss reasons */}
                                {tcPerf.loss_reasons.length > 0 && (
                                  <div style={{ marginTop: 8 }}>
                                    <p style={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-tertiary)', marginBottom: 5 }}>Top loss reasons</p>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                      {tcPerf.loss_reasons.slice(0, 4).map((lr, idx) => (
                                        <div key={lr.disposition} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10 }}>
                                          <span style={{ width: 82, color: 'var(--text-tertiary)', textAlign: 'right', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {lr.disposition.replace(/_/g, ' ')}
                                          </span>
                                          <div style={{ flex: 1, background: 'var(--surface-page)', borderRadius: 3, height: 6 }}>
                                            <div style={{ width: `${Math.round(lr.count / maxReasonCount * 100)}%`, height: 6, borderRadius: 3, background: BAR_COLORS[idx % BAR_COLORS.length] }} />
                                          </div>
                                          <span style={{ width: 14, color: 'var(--text-tertiary)', textAlign: 'right', flexShrink: 0 }}>{lr.count}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>—</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="table-card">
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th style={{ width: 24 }} />
                        <th>Lead</th>
                        <th>Customer</th>
                        <th>Vehicle</th>
                        <th>Status</th>
                        <th>Disposition Journey</th>
                        <th className="text-right">Days to Lost</th>
                        <th>Agent</th>
                        <th>Lost On</th>
                        <th className="text-right">Calls</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lostList.map(item => {
                        const expanded = expandedLead === item.lead_id
                        const narrative = narrativeCache[item.lead_id] ?? item.ai_journey_summary
                        return (
                          <React.Fragment key={item.lead_id}>
                            <tr
                              onClick={() => {
                                const next = expanded ? null : item.lead_id
                                setExpandedLead(next)
                                if (next !== null) handleLoadTcPerf(item.lead_id)
                              }}
                              style={{ cursor: 'pointer' }}
                            >
                              <td>
                                {expanded
                                  ? <ChevronDown className="h-3.5 w-3.5" style={{ color: 'var(--text-tertiary)' }} />
                                  : <ChevronRight className="h-3.5 w-3.5" style={{ color: 'var(--text-tertiary)' }} />}
                              </td>
                              <td className="font-mono text-xs" style={{ color: 'var(--brand-primary)' }}>
                                #{item.lead_id}
                              </td>
                              <td className="text-primary-data">{item.customer_name}</td>
                              <td className="font-mono text-xs" style={{ color: 'var(--text-secondary)' }}>
                                {item.vehicle_reg || '—'}
                              </td>
                              <td>
                                <span className={STATUS_BADGE[item.status] || 'badge-neutral'}>
                                  {item.status.replace(/_/g, ' ')}
                                </span>
                              </td>
                              <td>
                                <div className="flex flex-wrap gap-1">
                                  {item.disposition_sequence.map((d, i) => (
                                    <span key={i} className={`${DISPOSITION_BADGE[d] || 'badge-neutral'} text-xs`}
                                      style={{ fontSize: '0.65rem', padding: '1px 5px' }}>
                                      {d.replace(/_/g, ' ')}
                                    </span>
                                  ))}
                                  {item.disposition_sequence.length === 0 && (
                                    <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>—</span>
                                  )}
                                </div>
                              </td>
                              <td className="text-right tabular-nums" style={{ color: 'var(--text-secondary)' }}>
                                {item.days_to_lost !== null ? `${item.days_to_lost}d` : '—'}
                              </td>
                              <td style={{ color: 'var(--text-secondary)' }}>{item.assigned_to_name}</td>
                              <td className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                                {fmtDate(item.status_changed_at)}
                              </td>
                              <td className="text-right tabular-nums font-medium">{item.total_calls}</td>
                            </tr>

                            {/* Expanded detail panel */}
                            {expanded && (
                              <tr key={`${item.lead_id}-detail`}>
                                <td colSpan={10} style={{ padding: 0 }}>
                                  <div
                                    style={{
                                      background: 'var(--surface-subtle)',
                                      borderTop: '1px solid var(--border-default)',
                                      padding: '16px 20px 20px',
                                      display: 'grid',
                                      gridTemplateColumns: '1fr 220px',
                                      gap: 20,
                                    }}
                                  >
                                    {/* ── LEFT: timeline + AI summary + link ── */}
                                    <div className="space-y-4">

                                      {/* Disposition Journey timeline */}
                                      <div>
                                        <p className="text-xs font-semibold uppercase tracking-wider mb-3"
                                          style={{ color: 'var(--text-tertiary)' }}>
                                          Disposition Journey
                                        </p>
                                        {item.all_remarks.length === 0 ? (
                                          <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>No calls recorded</p>
                                        ) : (
                                          <div>
                                            {item.all_remarks.map((r, idx) => {
                                              const isLast = idx === item.all_remarks.length - 1
                                              return (
                                                <div key={r.call_no} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                                                  {/* dot + connector line column */}
                                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, width: 24 }}>
                                                    <div style={{
                                                      width: 24, height: 24, borderRadius: '50%',
                                                      background: 'var(--brand-primary-subtle)',
                                                      color: 'var(--brand-600)',
                                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                      fontSize: 11, fontWeight: 700, flexShrink: 0,
                                                    }}>
                                                      {r.call_no}
                                                    </div>
                                                    {!isLast && (
                                                      <div style={{ width: 2, flex: 1, minHeight: 16, background: 'var(--border-default)', borderRadius: 2, margin: '3px 0' }} />
                                                    )}
                                                  </div>
                                                  {/* content */}
                                                  <div style={{ flex: 1, minWidth: 0, paddingBottom: isLast ? 0 : 12 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                      <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{fmtDateTime(r.date)}</span>
                                                      {r.disposition && r.disposition !== 'NONE' && (
                                                        <span className={DISPOSITION_BADGE[r.disposition] || 'badge-neutral'}
                                                          style={{ fontSize: '0.65rem', padding: '1px 6px' }}>
                                                          {r.disposition.replace(/_/g, ' ')}
                                                        </span>
                                                      )}
                                                    </div>
                                                    {r.remarks && (
                                                      <p style={{ fontSize: 12, marginTop: 3, color: 'var(--text-primary)', lineHeight: 1.5 }}>{r.remarks}</p>
                                                    )}
                                                  </div>
                                                </div>
                                              )
                                            })}
                                          </div>
                                        )}
                                      </div>

                                      {/* AI narrative */}
                                      {aiExtractionEnabled && (
                                        <div className="border-t pt-3" style={{ borderColor: 'var(--border-default)' }}>
                                          <div className="flex items-center gap-2 mb-2">
                                            <Sparkles className="h-3.5 w-3.5" style={{ color: 'var(--brand-600)' }} />
                                            <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>
                                              AI Journey Summary
                                            </p>
                                          </div>
                                          {narrative ? (
                                            <p className="text-sm" style={{ color: 'var(--text-primary)', lineHeight: 1.6 }}>{narrative}</p>
                                          ) : narrativeLoading === item.lead_id ? (
                                            <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-tertiary)' }}>
                                              <div className="spinner" style={{ width: 12, height: 12 }} /> Generating summary…
                                            </div>
                                          ) : aiOnDemand ? (
                                            <button
                                              className="btn-secondary flex items-center gap-1.5 text-xs"
                                              onClick={e => { e.stopPropagation(); handleGenerateNarrative(item.lead_id) }}
                                            >
                                              <Sparkles className="h-3 w-3" /> Generate AI Summary
                                            </button>
                                          ) : (
                                            <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Summary will be available shortly…</p>
                                          )}
                                        </div>
                                      )}

                                      {/* View lead link */}
                                      <div className="border-t pt-3" style={{ borderColor: 'var(--border-default)' }}>
                                        <a href={`/leads/${item.lead_id}`}
                                          className="text-xs font-medium hover:underline"
                                          style={{ color: 'var(--brand-primary)' }}
                                          onClick={e => e.stopPropagation()}
                                        >
                                          View Full Lead →
                                        </a>
                                      </div>
                                    </div>

                                    {/* ── RIGHT: Telecaller performance ── */}
                                    <div>
                                      <p className="text-xs font-semibold uppercase tracking-wider mb-3"
                                        style={{ color: 'var(--text-tertiary)' }}>
                                        Telecaller Performance
                                      </p>

                                      {tcPerfLoading === item.lead_id ? (
                                        <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-tertiary)' }}>
                                          <div className="spinner" style={{ width: 12, height: 12 }} /> Loading…
                                        </div>
                                      ) : tcPerfCache[item.lead_id] ? (() => {
                                        const p = tcPerfCache[item.lead_id]
                                        const maxReasonCount = p.loss_reasons[0]?.count || 1
                                        return (
                                          <div className="card" style={{ padding: '12px 14px' }}>
                                            <p className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{p.name}</p>
                                            <p className="text-xs mb-3" style={{ color: 'var(--text-tertiary)' }}>Telecaller</p>

                                            {/* ── This lead ── */}
                                            <p style={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-tertiary)', marginBottom: 4 }}>This lead</p>
                                            {[
                                              { label: 'Calls made', value: String(item.total_calls) },
                                              { label: 'Days to lost', value: item.days_to_lost !== null ? `${item.days_to_lost}d` : '—' },
                                              { label: 'First contact', value: fmtDate(item.first_contacted_at) },
                                              { label: 'Last contact', value: fmtDate(item.last_contacted_at) },
                                              { label: 'Max discount', value: item.max_discount_offered !== null ? `${item.max_discount_offered}%` : '—' },
                                              { label: 'Final disposition', value: item.disposition_sequence.filter(d => d !== 'NONE').slice(-1)[0]?.replace(/_/g, ' ') ?? '—' },
                                            ].map(row => (
                                              <div key={row.label} className="flex justify-between items-center py-1.5"
                                                style={{ borderBottom: '1px solid var(--border-default)', fontSize: 11 }}>
                                                <span style={{ color: 'var(--text-tertiary)' }}>{row.label}</span>
                                                <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{row.value}</span>
                                              </div>
                                            ))}

                                            {/* ── MTD ── */}
                                            <p style={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-tertiary)', marginTop: 10, marginBottom: 4 }}>Month to date</p>
                                            {[
                                              { label: 'Leads assigned', value: p.assigned_mtd, color: undefined },
                                              { label: 'Policies issued', value: p.policies_mtd, color: 'var(--success-text)' },
                                              { label: 'Lost leads', value: p.lost_mtd, color: 'var(--danger-text)' },
                                              { label: 'Conversion rate', value: `${p.conversion_rate}%`, color: p.conversion_rate < 20 ? 'var(--warning-text)' : 'var(--success-text)' },
                                              { label: 'Avg calls / lost lead', value: p.avg_calls_per_lost !== null ? p.avg_calls_per_lost : '—', color: undefined },
                                              { label: 'Avg days to close', value: p.avg_days_to_lost !== null ? `${p.avg_days_to_lost}d` : '—', color: undefined },
                                            ].map(row => (
                                              <div key={row.label} className="flex justify-between items-center py-1.5"
                                                style={{ borderBottom: '1px solid var(--border-default)', fontSize: 11 }}>
                                                <span style={{ color: 'var(--text-tertiary)' }}>{row.label}</span>
                                                <span style={{ fontWeight: 700, color: row.color ?? 'var(--text-primary)' }}>{String(row.value)}</span>
                                              </div>
                                            ))}

                                            {/* Top loss reasons mini-bar */}
                                            {p.loss_reasons.length > 0 && (
                                              <div className="mt-3">
                                                <p className="text-xs font-semibold uppercase tracking-wider mb-2"
                                                  style={{ color: 'var(--text-tertiary)', fontSize: '0.6rem' }}>
                                                  Top loss reasons
                                                </p>
                                                <div className="space-y-1.5">
                                                  {(() => {
                                                    const BAR_COLORS = ['#ef4444','#f97316','#a855f7','#3b82f6']
                                                    return p.loss_reasons.slice(0, 4).map((lr, idx) => (
                                                      <div key={lr.disposition} className="flex items-center gap-2" style={{ fontSize: 10 }}>
                                                        <span style={{ width: 82, color: 'var(--text-tertiary)', textAlign: 'right', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                          {lr.disposition.replace(/_/g, ' ')}
                                                        </span>
                                                        <div style={{ flex: 1, background: 'var(--surface-page)', borderRadius: 3, height: 7 }}>
                                                          <div style={{
                                                            width: `${Math.round(lr.count / maxReasonCount * 100)}%`,
                                                            height: 7, borderRadius: 3,
                                                            background: BAR_COLORS[idx % BAR_COLORS.length],
                                                          }} />
                                                        </div>
                                                        <span style={{ width: 14, color: 'var(--text-tertiary)', textAlign: 'right', flexShrink: 0 }}>{lr.count}</span>
                                                      </div>
                                                    ))
                                                  })()}
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        )
                                      })() : (
                                        <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>—</p>
                                      )}
                                    </div>

                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Pagination */}
            {lostTotal > 50 && (
              <div className="flex items-center justify-center gap-3 mt-4">
                <button
                  className="btn-secondary text-xs"
                  disabled={lostPage === 1 || loadingLost}
                  onClick={() => loadLostLeads(lostPage - 1)}
                >
                  Previous
                </button>
                <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                  Page {lostPage} of {Math.ceil(lostTotal / 50)}
                </span>
                <button
                  className="btn-secondary text-xs"
                  disabled={lostPage >= Math.ceil(lostTotal / 50) || loadingLost}
                  onClick={() => loadLostLeads(lostPage + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          Tab: Telecaller Performance (operational stats + AI coaching)
      ══════════════════════════════════════════════════════════════════ */}
      {tab === 'telecaller-performance' && (() => {
        const renderDetail = (tc: TelecallerAIScoreData | undefined, insufficientData: boolean) => {
          if (!aiExtractionEnabled) return null
          const history = tc ? (coachingPlanHistory[tc.user_id] || []) : []
          const latest = history[0]
          const older = history.slice(1)
          const historyOpen = tc ? !!coachingHistoryOpen[tc.user_id] : false
          return (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-tertiary)' }}>
                AI Coaching Signal
              </p>
              {!tc ? (
                <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-tertiary)' }}>
                  <div className="spinner" style={{ width: 10, height: 10 }} /> Loading…
                </div>
              ) : insufficientData ? (
                <div className="alert-info" style={{ fontSize: 13 }}>
                  Not enough data yet — {tc.analyzed_call_count} of the {MIN_AI_SCORE_CALLS} analyzed calls needed for a score
                  ({tc.total_call_count} total calls).
                </div>
              ) : (
                <>
                  <p className="text-xs mb-2" style={{ color: 'var(--text-tertiary)' }}>
                    {tc.analyzed_call_count} of {tc.total_call_count} calls AI-analyzed
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {Object.entries(tc.weighted_sub_scores).map(([key, val]) => (
                      <div key={key}>
                        <p className="text-xs capitalize" style={{ color: 'var(--text-tertiary)' }}>
                          {key.replace(/_/g, ' ')}
                        </p>
                        <div className="h-1.5 rounded-full overflow-hidden mt-1" style={{ background: 'var(--surface-subtle)' }}>
                          <div className="h-full rounded-full" style={{ width: `${(val / 5) * 100}%`, background: 'var(--brand-primary)' }} />
                        </div>
                        <p className="text-xs mt-0.5" style={{ color: 'var(--text-primary)' }}>{val.toFixed(1)}</p>
                      </div>
                    ))}
                  </div>

                  {tc.top_flags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {tc.top_flags.map(f => (
                        <span key={f.flag} className="badge-warning text-xs">
                          {f.flag.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  )}

                  {tc.coaching_tip && (
                    <div className="mt-3 flex items-start gap-2" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                      <Sparkles className="h-3.5 w-3.5 shrink-0 mt-0.5" style={{ color: 'var(--brand-600)' }} />
                      <span>{tc.coaching_tip}</span>
                    </div>
                  )}

                  <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--border-default)' }}>
                    {latest && (
                      <div className="flex items-start gap-2" style={{ fontSize: 13, color: 'var(--text-primary)' }}>
                        <Sparkles className="h-3.5 w-3.5 shrink-0 mt-0.5" style={{ color: 'var(--brand-600)' }} />
                        <div>
                          <span>{latest.coaching_plan}</span>
                          <p className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                            Generated {fmtDateTime(latest.created_at)} · score at the time: {latest.weighted_overall?.toFixed(1) ?? '—'}/5
                            {' '}({latest.analyzed_call_count} calls)
                          </p>
                        </div>
                      </div>
                    )}

                    {coachingPlanLoading === tc.user_id && (
                      <div className="flex items-center gap-2 text-xs mt-2" style={{ color: 'var(--text-tertiary)' }}>
                        <div className="spinner" style={{ width: 10, height: 10 }} /> Generating coaching plan…
                      </div>
                    )}

                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                      <button
                        className="btn-secondary flex items-center gap-1.5"
                        style={{ fontSize: 12 }}
                        disabled={coachingPlanLoading === tc.user_id}
                        onClick={() => handleGenerateCoachingPlan(tc.user_id)}
                      >
                        <Sparkles className="h-3.5 w-3.5" /> {latest ? 'Regenerate' : 'Generate'} Coaching Plan
                      </button>
                      {older.length > 0 && (
                        <button
                          className="btn-secondary flex items-center gap-1"
                          style={{ fontSize: 12 }}
                          onClick={() => setCoachingHistoryOpen(prev => ({ ...prev, [tc.user_id]: !prev[tc.user_id] }))}
                        >
                          {historyOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                          History ({older.length})
                        </button>
                      )}
                    </div>

                    {historyOpen && older.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {older.map(p => (
                          <div key={p.id} className="text-xs" style={{ color: 'var(--text-secondary)', borderLeft: '2px solid var(--border-default)', paddingLeft: 8 }}>
                            <p>{p.coaching_plan}</p>
                            <p className="mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                              {fmtDateTime(p.created_at)} · {p.weighted_overall?.toFixed(1) ?? '—'}/5 ({p.analyzed_call_count} calls)
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )
        }

        const merged = (tcStats || []).map(stat => {
          const tc = aiScores?.find(a => a.user_id === stat.user_id)
          const insufficientData = aiExtractionEnabled && (!tc || tc.trend === 'insufficient_data')
          return { stat, tc, insufficientData }
        }).filter(({ stat }) => stat.name.toLowerCase().includes(tcSearch.toLowerCase()))

        const sorted = [...merged].sort((a, b) => {
          if (tcSort === 'name') return a.stat.name.localeCompare(b.stat.name)
          if (tcSort === 'conversion') return b.stat.conversion_rate - a.stat.conversion_rate
          return (b.tc?.weighted_overall ?? -1) - (a.tc?.weighted_overall ?? -1)
        })

        return (
          <div className="space-y-4">
            <SectionHeader title="Telecaller Performance" onRefresh={loadTcStats} loading={loadingAi} />

            {!tcStats && loadingAi && (
              <div className="card flex items-center gap-2 text-sm" style={{ color: 'var(--text-tertiary)' }}>
                <div className="spinner" style={{ width: 14, height: 14 }} /> Loading…
              </div>
            )}

            {tcStats && tcStats.length === 0 && (
              <div className="card text-sm" style={{ color: 'var(--text-tertiary)' }}>
                No telecallers found.
              </div>
            )}

            {tcStats && tcStats.length > 0 && (
              <>
                <div className="flex items-center gap-2 flex-wrap">
                  <input
                    type="text"
                    className="input"
                    style={{ flex: '1 1 200px' }}
                    placeholder="Search telecallers…"
                    value={tcSearch}
                    onChange={e => setTcSearch(e.target.value)}
                  />
                  <select
                    className="input"
                    style={{ width: isMobile ? '100%' : 180 }}
                    value={tcSort}
                    onChange={e => setTcSort(e.target.value as typeof tcSort)}
                  >
                    <option value="ai_score">Sort: AI score</option>
                    <option value="conversion">Sort: Conversion</option>
                    <option value="name">Sort: Name</option>
                  </select>
                </div>

                {isMobile ? (
                  /* ── Mobile: collapsed accordion rows — avoids N full-height cards of scroll ── */
                  <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                    {sorted.map(({ stat, tc, insufficientData }, idx) => {
                      const expanded = expandedTc === stat.user_id
                      const TrendIcon = tc?.trend === 'improving' ? TrendingUp : tc?.trend === 'declining' ? TrendingDown : Minus
                      const trendColor = tc?.trend === 'improving' ? 'var(--success-text)' : tc?.trend === 'declining' ? 'var(--danger-text)' : 'var(--text-tertiary)'
                      return (
                        <div key={stat.user_id} style={{ borderTop: idx > 0 ? '1px solid var(--border-default)' : undefined }}>
                          <button
                            className="flex items-center gap-2 w-full text-left"
                            style={{ padding: '10px 12px' }}
                            onClick={() => setExpandedTc(expanded ? null : stat.user_id)}
                          >
                            <div style={{
                              width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                              background: insufficientData ? 'var(--surface-subtle)' : 'var(--brand-primary-subtle)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: 11, fontWeight: 600, color: insufficientData ? 'var(--text-tertiary)' : 'var(--brand-primary)',
                            }}>
                              {stat.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                            </div>
                            <div className="flex-1" style={{ minWidth: 0 }}>
                              <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{stat.name}</p>
                              <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                                {stat.conversion_rate}% conversion · {stat.lost_leads} lost
                              </p>
                            </div>
                            {aiExtractionEnabled && (
                              tc && !insufficientData ? (
                                <span className="text-sm font-semibold" style={{ color: 'var(--brand-primary)' }}>{tc.weighted_overall?.toFixed(1)}</span>
                              ) : (
                                <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{tc ? `${tc.analyzed_call_count}/${MIN_AI_SCORE_CALLS}` : '—'}</span>
                              )
                            )}
                            {expanded ? <ChevronDown className="h-4 w-4" style={{ color: 'var(--text-tertiary)' }} /> : <ChevronRight className="h-4 w-4" style={{ color: 'var(--text-tertiary)' }} />}
                          </button>
                          {expanded && (
                            <div style={{ padding: '0 12px 16px' }}>
                              <div className="grid grid-cols-3 gap-3 mb-3">
                                <div>
                                  <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Total calls</p>
                                  <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{stat.total_calls}</p>
                                </div>
                                <div>
                                  <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Policies</p>
                                  <p className="text-sm font-semibold" style={{ color: 'var(--success-text)' }}>{stat.policies_issued}</p>
                                </div>
                                <div>
                                  <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Lost</p>
                                  <p className="text-sm font-semibold" style={{ color: 'var(--danger-text)' }}>{stat.lost_leads}</p>
                                </div>
                              </div>
                              {tc && !insufficientData && (
                                <div className="flex items-center gap-1 mb-3">
                                  <span title={tc.trend}><TrendIcon className="h-3.5 w-3.5" style={{ color: trendColor }} /></span>
                                  <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Trend: {tc.trend}</span>
                                </div>
                              )}
                              {renderDetail(tc, insufficientData)}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  /* ── Desktop: compact table + expand-on-click row ── */
                  <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                    <div className="overflow-x-auto">
                      <table className="data-table" style={{ width: '100%' }}>
                        <thead>
                          <tr>
                            <th>Telecaller</th>
                            <th>AI score</th>
                            <th>Calls</th>
                            <th>Conversion</th>
                            <th>Policies</th>
                            <th>Lost</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {sorted.map(({ stat, tc, insufficientData }) => {
                            const expanded = expandedTc === stat.user_id
                            const TrendIcon = tc?.trend === 'improving' ? TrendingUp : tc?.trend === 'declining' ? TrendingDown : Minus
                            const trendColor = tc?.trend === 'improving' ? 'var(--success-text)' : tc?.trend === 'declining' ? 'var(--danger-text)' : 'var(--text-tertiary)'
                            return (
                              <React.Fragment key={stat.user_id}>
                                <tr
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => setExpandedTc(expanded ? null : stat.user_id)}
                                >
                                  <td style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{stat.name}</td>
                                  <td>
                                    {aiExtractionEnabled ? (
                                      tc && !insufficientData ? (
                                        <span className="flex items-center gap-1.5">
                                          <span style={{ color: 'var(--brand-primary)', fontWeight: 600 }}>{tc.weighted_overall?.toFixed(1)}</span>
                                          <span title={tc.trend}><TrendIcon className="h-3.5 w-3.5" style={{ color: trendColor }} /></span>
                                        </span>
                                      ) : (
                                        <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                                          — ({tc ? `${tc.analyzed_call_count}/${MIN_AI_SCORE_CALLS}` : '…'})
                                        </span>
                                      )
                                    ) : <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
                                  </td>
                                  <td style={{ color: 'var(--text-secondary)' }}>{stat.total_calls}</td>
                                  <td style={{ color: 'var(--text-secondary)' }}>{stat.conversion_rate}%</td>
                                  <td style={{ color: 'var(--success-text)' }}>{stat.policies_issued}</td>
                                  <td style={{ color: 'var(--danger-text)' }}>{stat.lost_leads}</td>
                                  <td style={{ textAlign: 'right' }}>
                                    {expanded ? <ChevronDown className="h-4 w-4" style={{ color: 'var(--brand-primary)' }} /> : <ChevronRight className="h-4 w-4" style={{ color: 'var(--text-tertiary)' }} />}
                                  </td>
                                </tr>
                                {expanded && (
                                  <tr>
                                    <td colSpan={7} style={{ background: 'var(--surface-subtle)', padding: '16px 20px' }}>
                                      {renderDetail(tc, insufficientData)}
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )
      })()}

      {/* ══════════════════════════════════════════════════════════════════
          Tab: Exports
      ══════════════════════════════════════════════════════════════════ */}
      {tab === 'exports' && (
        <div className="space-y-4">
          <div className="card max-w-md space-y-5">
            <div className="flex items-center gap-3">
              <div className="rounded-lg p-2.5" style={{ background: 'var(--info-bg)' }}>
                <FileText className="h-5 w-5" style={{ color: 'var(--info-text)' }} />
              </div>
              <div>
                <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Call Detail Report
                </p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                  CSV export of all calls — agent, customer, disposition, duration, date
                </p>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Period</label>
              <div className="flex gap-2 flex-wrap">
                {[7, 14, 30, 90].map(d => (
                  <button
                    key={d}
                    onClick={() => setExportDays(d)}
                    className={exportDays === d ? 'chip active' : 'chip'}
                  >
                    Last {d} days
                  </button>
                ))}
              </div>
            </div>

            <button
              className="btn-primary flex items-center gap-2"
              onClick={handleExport}
              disabled={exporting}
            >
              {exporting ? (
                <div className="spinner" style={{ width: 14, height: 14 }} />
              ) : (
                <Download className="h-4 w-4" />
              )}
              {exporting ? 'Preparing…' : `Download CSV (last ${exportDays} days)`}
            </button>

            <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
              File will include: Call ID, Customer, Mobile, Agent, Disposition, Duration, Remarks, Date.
              Max 365 days per export.
            </p>
          </div>

          {/* Future exports placeholder */}
          <div className="card max-w-md" style={{ opacity: 0.5 }}>
            <div className="flex items-center gap-3">
              <div className="rounded-lg p-2.5" style={{ background: 'var(--surface-subtle)' }}>
                <IndianRupee className="h-5 w-5" style={{ color: 'var(--text-tertiary)' }} />
              </div>
              <div>
                <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Premium Collection Report
                </p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                  Coming soon
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
